/**
 * Certificações pendentes — colunas do Design UX T3 (E52.F3).
 *
 * A tabela não decide nada: ordem, prazo, atraso e "vence hoje" chegam prontos
 * do servidor (BR-10). O atraso é sinalizado **sobre** o estado corrente —
 * prazo é atributo do item, não estado (PRD §6), então a linha atrasada
 * continua mostrando "Requerida" ou "Em revisão".
 *
 * E54.F8.T07: a tabela passou ao primitivo do pacote (cabeçalho em caixa alta
 * de 11px sobre hairline, realce de linha a 4%), código do caso, CCB e prazo em
 * mono. O prazo atrasado continua distinguível pelo **texto** que o servidor
 * manda — a cor é reforço, nunca a única portadora.
 */
import {
  Badge,
  Blueprint,
  Button,
  MonoData,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { CertificacaoPendente, StatusCertificacao } from '@/lib/certificacao';
import { rotuloPrazo } from '@/lib/sla';
// E59: rótulo e tom vêm da fonte única do veredito — esta tela e o portal
// não podem dizer coisas diferentes sobre o mesmo estado.
import { rotuloDoResultado, tomDoResultado } from '@/lib/veredito';

const t = ptBR.certificacao;

const STATUS_LABEL: Record<StatusCertificacao, string> = {
  requerida: t.statusRequerida,
  em_revisao: t.statusEmRevisao,
  laudo_entregue: t.statusLaudoEntregue,
};

export interface PendentesTabelaProps {
  itens: CertificacaoPendente[];
  itemAberto: string | null;
  abrindoId: string | null;
  onAbrirRevisao: (item: CertificacaoPendente) => void;
}

export function PendentesTabela({
  itens,
  itemAberto,
  abrindoId,
  onAbrirRevisao,
}: PendentesTabelaProps) {
  if (itens.length === 0) {
    return (
      <Blueprint data-testid="pendentes-empty" className="px-sc-4 py-sc-8 text-center">
        <p className="text-body-sm font-medium text-fg">{t.pendentesEmptyTitle}</p>
        <p className="mt-1 text-body-sm text-fg-muted">{t.pendentesEmptyHint}</p>
      </Blueprint>
    );
  }
  const agora = new Date();
  return (
    <Blueprint>
      <div className="overflow-x-auto [contain:layout]">
        <Table data-testid="pendentes-tabela">
          <TableHeader>
            <TableRow>
              <TableHead>{t.colCaso}</TableHead>
              <TableHead>{t.colExame}</TableHead>
              <TableHead>{t.colRequeridaPor}</TableHead>
              <TableHead>{t.colProducao}</TableHead>
              <TableHead>{t.colPrazo}</TableHead>
              <TableHead>{t.colAcao}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {itens.map((item) => (
              <TableRow key={item.requisicao_id} data-testid={`pendente-${item.requisicao_id}`}>
                <TableCell>
                  <MonoData>{item.case_display_code}</MonoData>
                  <span className="text-fg-muted"> · </span>
                  <MonoData>{item.ccb_number ?? t.semValor}</MonoData>
                  {/* E60.F3: sem a marca, o certificador veria a lista fora da
                      ordem de prazo prometida e sem explicação. */}
                  {item.prioritario === true && (
                    <Badge
                      variant="warning"
                      className="ml-2 align-middle"
                      data-testid={`certificacao-prioritario-${item.requisicao_id}`}
                    >
                      {t.prioritarioBadge}
                    </Badge>
                  )}
                  <p className="mt-0.5 text-caption text-fg-muted">
                    {item.escritorio_nome ?? t.semValor} · {STATUS_LABEL[item.status]}
                  </p>
                </TableCell>
                <TableCell>
                  <Badge variant={tomDoResultado(item.resultado_exame)}>
                    {rotuloDoResultado(item.resultado_exame)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="text-body-sm text-fg">{item.requerida_por}</span>
                  <p className="mt-0.5 text-caption text-fg-muted">
                    {item.ambiente === 'banco' ? t.ambienteBanco : t.ambienteEscritorio}
                  </p>
                </TableCell>
                <TableCell className="text-body-sm text-fg-muted">
                  {item.producao_origem ?? t.semValor}
                </TableCell>
                <TableCell>
                  {/* Prazo é dado pericial e é resultado de contagem: mono, em
                      tag reta. Atrasado entra na rampa negativa — mas quem diz
                      que atrasou é o texto do rótulo. */}
                  <Badge
                    variant={item.atrasado ? 'danger' : 'outline'}
                    className="font-mono"
                    data-testid={`prazo-${item.requisicao_id}`}
                  >
                    {rotuloPrazo(item.sla_due_at, agora)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    data-testid={`abrir-revisao-${item.requisicao_id}`}
                    loading={abrindoId === item.requisicao_id}
                    disabled={abrindoId !== null}
                    aria-expanded={itemAberto === item.requisicao_id}
                    onClick={() => onAbrirRevisao(item)}
                  >
                    {abrindoId === item.requisicao_id ? t.abrindoRevisao : t.abrirRevisao}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Blueprint>
  );
}
